import { DaggerProps, ReadProps } from "../../@types/viewerati";
import { FilterColors, FilterFonts } from "../../state/filter";
import { HostId, IsPreviewGenerator, ReadMode } from "../../state/ui";
import {
	IsInPlayWrightTest,
	PageElementsFamily,
	PageSizeWithPaddingSelector,
	createLocation,
} from "../../state/page";
import { ReactElement, useContext, useRef, useState } from "react";
import { SDK_Read, colors, strings } from "@quickpoint.me/shared";
import styled, { DefaultTheme, ThemeProvider } from "styled-components";

import { ElementProps } from "../../functionalComponents/RenderPage";
import { IsInFlexBoxContext } from "../displayElements/Cell";
import MarkDownToTag from "../../lib/markdown/MarkDown";
import { NeuroDivergentFontMultiplier } from "../../state/accessibility";
import { OwnerName } from "../../state/selectors";
import { PositionContext } from "../displayElements/DisplayElements";
import createTestId from "../../lib/createTestId";
import gsap from "gsap";
import { mainCssRules } from "../../lib/cssTemplates";
import { useGSAP } from "@gsap/react";
import { useRecoilValue } from "recoil";

gsap.registerPlugin(useGSAP);

const readFilter = (theme: DefaultTheme): SDK_Read => {
	return theme.read;
};

const StyledRead = styled.section<ReadProps & { $fontMultiplier: number; $baseFontSizeRaw: number }>`
  overflow: hidden;
  max-height: 50svh;
${(props) =>
	`${mainCssRules({
		baseFontSize: props.$baseFontSize,
		baseFontSizeRaw: props.$baseFontSizeRaw,
		fonts: props.$fonts,
		theme: props.theme,
		fontMultiplier: props.$fontMultiplier,
	})}`}
  display: ${(props) => (props.$readMode ? "flex" : "none")};
  align-items: center;
  height: auto;
  border-end-start-radius : 0;
  border-end-end-radius: 0;
  width: ${(props) => `${props.$pageSize.width}px`};
  z-index:60;
  position: absolute; // important to keep this here.  
  top: ${(props) => props.$top};
  visibility: hidden;
  background: linear-gradient(180deg, #fdf4fc, #fff 60%);
`;

const StyledDagger = styled.div<DaggerProps>`
  display: flex;
  align-items: center;
  justify-self: end;
  visibility: visible;
  padding:  ${(props) => `${props.$pageWidth / 200}px`};
  margin:  ${(props) => `${props.$pageWidth / 200}px`};
  color: ${(props) =>
		props.$daggerColor ? props.$daggerColor : colors.tufte.black};
  cursor: pointer;

`;

const Read = ({
	atomId,
	pageIndex,
	type,
}: ElementProps): ReactElement<ElementProps> => {
	const [show, setShow] = useState(false);
	const pageSize = useRecoilValue(PageSizeWithPaddingSelector);
	const key = createLocation(pageIndex, atomId);
	const baseFontSize = pageSize.baseFontSize;
	const daggerRef = useRef<HTMLDivElement>(null);
	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const filterColors = useRecoilValue(FilterColors);
	const fontMultiplier = useRecoilValue(NeuroDivergentFontMultiplier);
	const fonts = useRecoilValue(FilterFonts);
	const hostId = useRecoilValue(HostId);
	const inReadMode = useRecoilValue(ReadMode);
	const isInFlexBox = useContext(IsInFlexBoxContext);
	const isInPlaywrightTest = useRecoilValue(IsInPlayWrightTest);
	const isPreviewer = useRecoilValue(IsPreviewGenerator);
	const ownerName = useRecoilValue(OwnerName);
	const readRef = useRef<HTMLDivElement>(null);
	const position = useContext(PositionContext);
	const readMode = useRecoilValue(ReadMode);
	const tapped = elementData.tapped;
	const daggerColor = elementData.modifier?.["color:"]
		? // @ts-expect-error
			filterColors[elementData.modifier?.["color:"]]
		: colors.tufte.black;
	// const displayRead = (position === "top" || position === "left" && direction === "forward")
	//   || (position === "bottom" || position === "right" && direction === "back")
	//   || isInPlaywrightTest;

	// Animate Read
	useGSAP(
		() => {
			if (!readRef.current) return;
			if (show) {
				const readHeight = readRef.current.offsetHeight;
				if (isInPlaywrightTest) {
					gsap.set(readRef.current, {
						top: pageSize.height - readHeight,
						height: "auto",
						autoAlpha: 1,
					});
				} else {
					gsap.set(readRef.current, { top: pageSize.height - readHeight });
					gsap.to(readRef.current, { height: "auto", autoAlpha: 1 });
				}
			}
		},
		{
			dependencies: [
				show,
				pageSize.x0,
				pageSize.documentHeight,
				pageSize.height,
			],
		},
	);

	const showHide = () => {
		setShow(!show);
	};

	// Animate Arrows
	useGSAP(
		() => {
			if (!daggerRef.current) return;
			if (show) {
				gsap.to(daggerRef.current, { rotate: 90 });
			} else {
				gsap.to(daggerRef.current, { rotate: 0 });
			}
		},
		{ dependencies: [show] },
	);

	const content =
		typeof elementData.content === "string"
			? MarkDownToTag({
					text: elementData.content,
					ownerName,
					modifier: elementData.modifier,
					hostId,
					tapped,
				})
			: elementData.content;

	const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		showHide();
	};
	if (!elementData.content) return <></>;

	const ReadElements = () => (
		<ThemeProvider theme={readFilter}>
			<StyledRead
				$baseFontSize={baseFontSize}
				$baseFontSizeRaw={pageSize.baseFontSizeRaw}
				$elementIndex={atomId}
				$filterColors={filterColors}
				$fonts={fonts}
				$fontMultiplier={fontMultiplier}
				$isInFlexBox={isInFlexBox}
				$left={pageSize.x0}
				$modifier={elementData.modifier}
				$onePercentHeight={pageSize.onePercentHeight}
				$onePercentWidth={pageSize.onePercentWidth}
				$pageSize={pageSize}
				$isInPortrait={!pageSize.landscape}
				$readMode={readMode}
				$show={show}
				$tapped={tapped}
				$top={pageSize.height}
				aria-hidden={!elementData.tapped}
				className={`${strings.css.pageElement} read`}
				data-atom-id={atomId}
				data-testid={createTestId({ atomId, position, type, isPreviewer })}
				onClick={handleClick}
				ref={readRef}
			>
				<div className="content" aria-hidden={!show}>
					{content}
				</div>
			</StyledRead>
		</ThemeProvider>
	);

	if (!inReadMode) {
		return <></>;
	}

	return (
		<>
			<StyledDagger
				className="read-dagger"
				$pageWidth={pageSize.width}
				ref={daggerRef}
				$daggerColor={daggerColor}
				data-testid={`dagger-${createTestId({
					atomId,
					position,
					type,
					isPreviewer,
				})}`}
				onClick={handleClick}
			>
				†
			</StyledDagger>
			<ReadElements />
		</>
	);
};

export default Read;
